
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { AccountEmail } from "@/components/account/AccountEmail";
import { NameEditor } from "@/components/account/NameEditor";
import { SubscriptionInfo } from "@/components/account/SubscriptionInfo";
import { AccountActions } from "@/components/account/AccountActions";
import { BusinessNameEditor } from "@/components/account/BusinessNameEditor";

export default function Account() {
  const navigate = useNavigate();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session?.user?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
    staleTime: 1000 * 60 * 5,
  });

  // Fallback for email if not in profile
  const userEmail = profile?.email || session?.user?.email;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 pt-24">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    navigate('/');
    return null;
  }

  if (!profile) return null;

  const isTrainee = profile.role === 'trainee';
  const isAdmin = profile.role === 'admin';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 pt-24">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Account Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <AccountEmail email={userEmail} />
            
            <NameEditor 
              userId={session.user.id}
              firstName={profile.first_name}
              lastName={profile.last_name}
            />

            {isAdmin && (
              <BusinessNameEditor 
                userId={session.user.id}
                businessName={profile.company}
              />
            )}

            {!isTrainee && (
              <SubscriptionInfo 
                subscriptionTier={profile.subscription_tier}
                subscriptionUpdatedAt={profile.subscription_updated_at}
              />
            )}

            <AccountActions 
              userId={session.user.id}
              userEmail={userEmail}
              subscriptionTier={profile.subscription_tier}
              onAccountDeleted={() => navigate('/')}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
